import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Table,
  Td,
  StyledTr,
  TableWrapper,
  TableHeaderBox,
  Input,
  Button,
  SortableHeader,
  SortArrow
} from '../styles/TableStyles';

const formatCellValue = (value, format) => {
  if (format === 'currency' && !isNaN(parseFloat(value))) {
    return `$${parseFloat(value).toFixed(2)}`;
  }
  return value ?? '';
};

const StandardTableLayout = ({
  title,
  showForm,
  onToggleForm,
  onSubmit,
  columns,
  formData,
  handleChange,
  children,
  rows,
  renderCell = null,
  getRowProps,
  hideActionsColumn = false,
  renderActions = () => null,
  sortConfig = null,
  onSort = () => {},
  searchValue = '',
  onSearchChange = () => {},
  isEditable = true,
  editingId = null,
  disableSearch = false,
  submitting = false,
  fitToViewport = false,
  viewportOffset = 140,
}) => {
  // Debounced search for consistent UX across pages
  const [internalSearch, setInternalSearch] = useState(searchValue);
  const debounceRef = useRef(null);
  useEffect(() => setInternalSearch(searchValue), [searchValue]);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearchChange({ target: { value: internalSearch } }), 350);
    return () => clearTimeout(debounceRef.current);
  }, [internalSearch, onSearchChange]);

  // Focus first focusable input when Add form opens
  const formRef = useRef(null);
  useEffect(() => {
    if (showForm && formRef.current) {
      const first = formRef.current.querySelector('input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (first && typeof first.focus === 'function') first.focus();
    }
  }, [showForm]);

  // Filter to avoid showing duplicate edit and display rows
  const visibleRows = rows.filter(
    (row) => !(editingId && editingId === row.UniqueID && showForm)
  );

  // Freeze column widths while editing to avoid layout shift
  const tableRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState(null);
  const [widthsFrozen, setWidthsFrozen] = useState(false);

  useEffect(() => {
    const isEditing = Boolean(isEditable && editingId);
    if (isEditing && !widthsFrozen) {
      const tableEl = tableRef.current;
      if (tableEl) {
        const firstRow = tableEl.querySelector('tbody tr');
        const cells = firstRow ? firstRow.children : tableEl.querySelectorAll('thead th');
        const widths = Array.from(cells).map((el) => Math.ceil(el.getBoundingClientRect().width));
        if (widths.length) {
          setColumnWidths(widths);
          setWidthsFrozen(true);
        }
      }
    } else if (!isEditing && widthsFrozen) {
      setColumnWidths(null);
      setWidthsFrozen(false);
    }
  }, [isEditable, editingId, widthsFrozen]);

  const containerStyle = fitToViewport
    ? {
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${viewportOffset}px)`,
        minHeight: 0,
      }
    : undefined;

  const wrapperStyle = fitToViewport
    ? { flex: '1 1 auto', minHeight: 0 }
    : undefined;

  return (
    <Container style={containerStyle}>
      <TableHeaderBox>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>{title}</h1>
          {children}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {isEditable && (
            <Button type="button" aria-label="Add Entry" onClick={onToggleForm} disabled={submitting || Boolean(editingId)}>
              {showForm ? 'Cancel' : 'Add Entry'}
            </Button>
          )}
          {!disableSearch && (
            <Input
              type="text"
              placeholder="Search..."
              aria-label="Search"
              value={internalSearch}
              onChange={(e) => setInternalSearch(e.target.value)}
              style={{ maxWidth: '240px' }}
            />
          )}
        </div>
      </TableHeaderBox>

      <form ref={formRef} onSubmit={onSubmit}>
        <TableWrapper style={wrapperStyle}>
          <Table ref={tableRef}>
            {columnWidths && (
              <colgroup>
                {columnWidths.map((w, i) => (
                  <col key={`col-${i}`} style={{ width: w, minWidth: w, maxWidth: w }} />
                ))}
              </colgroup>
            )}
            <thead>
              <tr>
                {columns.map(({ key, label, width }, idx) => (
                  <SortableHeader
                    key={key}
                    onClick={() => onSort(key)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Sort by ${label}`}
                    style={columnWidths
                      ? { width: columnWidths[idx], minWidth: columnWidths[idx], maxWidth: columnWidths[idx] }
                      : (width ? { width } : undefined)}
                  >
                    {label}
                    {sortConfig?.key === key && (
                      <SortArrow direction={sortConfig.direction} />
                    )}
                  </SortableHeader>
                ))}
                {!hideActionsColumn && isEditable && (
                  <SortableHeader
                    style={columnWidths && columnWidths.length > columns.length
                      ? { width: columnWidths[columns.length], minWidth: columnWidths[columns.length], maxWidth: columnWidths[columns.length] }
                      : undefined}
                  >
                    Actions
                  </SortableHeader>
                )}
              </tr>
            </thead>

            <tbody>
              {/* Add Entry Form */}
              {showForm && isEditable && (
                <StyledTr $index={-1}>
                  {columns.map(({ key }, idx) => (
                    <Td key={key} style={columnWidths ? { width: columnWidths[idx], minWidth: columnWidths[idx], maxWidth: columnWidths[idx] } : undefined}>
                      {renderCell ? renderCell(key, formData, handleChange) : null}
                    </Td>
                  ))}
                  {!hideActionsColumn && (
                    <Td style={columnWidths && columnWidths.length > columns.length ? { width: columnWidths[columns.length], minWidth: columnWidths[columns.length], maxWidth: columnWidths[columns.length] } : undefined}>
                      <Button aria-label="Save" type="submit" disabled={submitting}>Submit</Button>
                    </Td>
                  )}
                </StyledTr>
              )}

              {/* Data Rows */}
              {visibleRows.map((row, index) => {
                const rowProps = {
                  ...(getRowProps ? getRowProps(row, index) : {}),
                  $index: index,
                };
                const isEditingRow =
                  isEditable && editingId && editingId === row.UniqueID;

                return (
                  <StyledTr key={row.UniqueID} {...rowProps}>
                    {columns.map(({ key, format, numeric, center, width }, idx) => (
                      <Td
                        key={key}
                        numeric={numeric}
                        style={{
                          ...(width ? { width } : {}),
                          ...(columnWidths ? { width: columnWidths[idx], minWidth: columnWidths[idx], maxWidth: columnWidths[idx] } : {}),
                          ...(center
                            ? { textAlign: 'center' }
                            : numeric
                            ? { textAlign: 'right' }
                            : { textAlign: 'left' })
                        }}
                      >
                        {isEditingRow
                          ? (renderCell ? renderCell(key, formData, handleChange) : formatCellValue(formData[key], format))
                          : (renderCell ? renderCell(key, row) : formatCellValue(row[key], format))}
                      </Td>
                    ))}

                    {!hideActionsColumn && isEditable && (
                      <Td style={columnWidths && columnWidths.length > columns.length ? { width: columnWidths[columns.length], minWidth: columnWidths[columns.length], maxWidth: columnWidths[columns.length] } : undefined}>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          {renderActions(row)}
                        </div>
                      </Td>
                    )}
                  </StyledTr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </form>
    </Container>
  );
};

export default StandardTableLayout;